<template>
  <w-text-field
    v-if="!readonly"
    v-bind="$props"
    :key="key"
    ref="textField"
    :value="inputValue"
    :placeholder="internalPlaceholder"
    @change="formatValue"
    @keydown="onKeyDown"
  />
  <w-read-only-text-field
    v-else
    v-bind="$props"
    :value="inputValue"
  />
</template>

<script>
// components
import { VTextField } from 'vuetify/lib';

// mixins
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';

// helpers
import temporalAmountFormatService from '@/services/temporal-amount-format-service.js';
import { DEFAULT_TIME, DEFAULT_TIME_OF_DAY } from '@/services/temporal-amount-format-service.js';
import dateFormatter from '@/services/formatter_services/date-formatter';
import SPECIAL_CHARACTERS from '@/utils/enum/SPECIAL_CHARACTERS';
import VARIANTS_OF_KEYS from '@/utils/enum/VARIANTS_OF_KEY';

const COMMA_AND_POINT_COLON = [
  SPECIAL_CHARACTERS.COMMA,
  SPECIAL_CHARACTERS.POINT,
  SPECIAL_CHARACTERS.COLON
];

const CHAR_WHITELIST = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const ALWAYS_ALLOWED = [
  VARIANTS_OF_KEYS.ARROWLEFT.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.ARROWRIGHT.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.DELETE.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.BACKSPACE.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.TAB.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.CONTROL.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.C.ALPHABETICAL.join(),
  VARIANTS_OF_KEYS.V.ALPHABETICAL.join(),
];

/**
 * @component WorkTimeField
 * @description Time input field based on `VTextField` with input validation
 * for time formats (HH:MM). Supports working time format and free time input.
 * All Vuetify `VTextField` props are inherited.
 *
 * @prop {Number} [grid=0] - Minute grid for input.
 * @prop {Boolean} [isWorkingTime=false] - Use working time format (instead of time of day).
 * @prop {Boolean} [readonly] - Read-only mode.
 *
 * @emits {String} input - Emitted when a valid time is entered.
 */
export default {
  name: 'WorkTimeField',
  mixins: [isRequired, pendingAble],
  props: {
    grid: { type: Number, default: 0 },
    isWorkingTime: { type: Boolean, default: false },
    readonly: Boolean,
    ...VTextField.options.props
  },
  data() {
    return {
      key: 0
    };
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.key++;
        this.$emit('input', v);
      }
    },
    internalPlaceholder() {
      return this.isWorkingTime ? DEFAULT_TIME : DEFAULT_TIME_OF_DAY;
    }
  },
  methods: {
    onKeyDown(event) {
      if (
        ALWAYS_ALLOWED.includes(event.key) === true ||
        COMMA_AND_POINT_COLON.includes(event.key) === true
      ) {
        return;
      }

      if (CHAR_WHITELIST.includes(event.key) === false) {
        event.preventDefault();
      }

      if (event.keyCode === VARIANTS_OF_KEYS.PLUS.KEY_CODE) {
        this.addTimegrid();
      }

      if (event.keyCode === VARIANTS_OF_KEYS.MINUS.KEY_CODE) {
        this.substractTimegrid();
      }
    },

    formatValue(value) {
      this.inputValue = this.formatTime(value);
      this.$emit('change', this.inputValue);
    },

    formatTime(time) {
      let minutes = temporalAmountFormatService.mapTimeAmountToMinutes(time);
      if (this.isWorkingTime)
        return temporalAmountFormatService.formatMinutesToWorkingHours(minutes);
      return dateFormatter.formatMinutesToWorkingHours(minutes);
    },

    addTimegrid() {
      this.inputValue = temporalAmountFormatService.addMinutesToTime(
        this.inputValue,
        this.grid
      );
    },

    substractTimegrid() {
      this.inputValue = temporalAmountFormatService.substractMinutesOfTime(
        this.inputValue,
        this.grid
      );
    },
    focus() {
      this.$refs.textField.focus();
    }
  }
};
</script>
